/**
 * Meldingen rechtsonder zodra een fan iets nieuws stuurt.
 *
 * Oud laat die zien als "New message — <model>", waar je ook bent in het
 * dashboard. Dat is niet cosmetisch: een chatter die op Earnings staat te
 * kijken mist anders dat er iemand zit te wachten.
 *
 * De bron is de wachtrij die de zijbalk toch al ophaalt — geen extra verzoek
 * dus. Er wordt gemeld bij een gesprek dat nieuw in de rij komt, en bij een
 * gesprek dat al in de rij stond maar een nieuwer bericht heeft gekregen.
 *
 * 18-10-2026 (Jordy): nooit twee keer dezelfde melding. Een gesprek dat even
 * uit de opgehaalde rij valt en terugkomt met hetzelfde bericht, werd opnieuw
 * gemeld; daarom onthouden we per sessie elk (gesprek, bericht-moment) dat we
 * al gezien hebben. Een klik op de melding opent dat gesprek.
 *
 * 18-10-2026 (Jordy, multi-chatter inbox): antwoordt een fan in een gesprek
 * dat NU van mij is (ik antwoordde, 5 minuten), dan is de melding GROEN en
 * alleen bij mij: "Fan replied — …". Een gesprek dat nu van een andere
 * chatter is, komt voor mij niet eens in de rij (de server laat het weg).
 */
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useActions } from './actions'
import { zetKlokRijen } from './claimKlok'

/** Sleutel voor één fanbericht: gesprek + moment van het laatste fanbericht. */
export const meldSleutel = (id: number, moment: string | null | undefined) => `${id}|${String(moment ?? '')}`

/** Welke rijen een melding krijgen; `gezien` wordt bijgewerkt. Zonder React (getest met node). */
export function teMelden<R extends { id: number; last_fan_message_at?: string | null }>(
  rijen: R[], gezien: Set<string>, eersteRonde: boolean): R[] {
  const uit: R[] = []
  for (const r of rijen) {
    const k = meldSleutel(r.id, r.last_fan_message_at)
    if (gezien.has(k)) continue
    gezien.add(k)
    if (!eersteRonde && r.last_fan_message_at) uit.push(r)
  }
  return uit
}

export interface WachtRij {
  id: number
  stage_name?: string | null
  model_slug?: string | null
  last_fan_message_at?: string | null
  fan_display_name?: string | null
  fan_username?: string | null
  last_message?: string | null
  inbox_claim_mine?: boolean | null
  inbox_claim_active?: boolean | null
  inbox_claim_state?: string | null
  inbox_claim_until?: string | null
}

export function useNieuweBerichten(rijen: WachtRij[] | undefined): void {
  // 18-10-2026 (Jordy): ook de 2-minutenwaarschuwing en de globale klokteller
  // (components/KlokTeller.tsx) hangen aan dezelfde rijen.
  useClaimWaarschuwing(rijen)
  useEffect(() => { zetKlokRijen(rijen) }, [rijen])
  const { notify } = useActions()
  const navigate = useNavigate()
  // Alles wat we al gezien hebben, per (gesprek, moment), voor deze sessie.
  // De eerste ronde na het laden meldt niets, anders krijg je bij elke
  // pagina-verversing een scherm vol meldingen over berichten van gisteren.
  const gezien = useRef<Set<string>>(new Set())
  const eerste = useRef(true)

  useEffect(() => {
    if (!rijen) return
    const nieuw = teMelden(rijen, gezien.current, eerste.current)
    eerste.current = false
    for (const rij of nieuw) {
      const naam = rij.stage_name || rij.model_slug || 'unknown model'
      // Toon ook wie het stuurde en wat: een chatter wil in de melding zelf de
      // fannaam en de tekst zien, niet alleen het model. Oud deed dat ook.
      const fan = rij.fan_display_name || rij.fan_username || ''
      const tekst = (rij.last_message || '').replace(/\s+/g, ' ').trim()
      const kort = tekst.length > 80 ? tekst.slice(0, 80) + '…' : tekst
      const kop = fan ? `${naam} · ${fan}` : naam
      const vanMij = !!(rij.inbox_claim_mine && rij.inbox_claim_active)
      const soort = vanMij ? 'Fan replied' : 'New message'
      notify(kort ? `${soort} — ${kop}: ${kort}` : `${soort} — ${kop}`, true,
             { onClick: () => navigate(`/conversations/show/${rij.id}`), groen: vanMij })
    }
  }, [rijen, notify, navigate])
}

/** Zoveel vóór de deadline komt de waarschuwing. */
export const WAARSCHUW_MS = 2 * 60 * 1000

/**
 * 18-10-2026 (Jordy): wanneer moet de 2-minutenwaarschuwing komen? Alleen bij de
 * EIGENAAR, alleen als de fan antwoordde (stand "replied"), en één keer per
 * fan-antwoordcyclus: de sleutel is gesprek + deadline. Antwoordt de chatter,
 * dan is de stand weer "waiting" (geen waarschuwing); een nieuw fanbericht geeft
 * een nieuwe deadline en dus een nieuwe cyclus. Geeft [sleutel, tijdstip] terug.
 * Zonder React (getest met node).
 */
export function waarschuwMomenten(rijen: WachtRij[]): [string, number, WachtRij][] {
  const uit: [string, number, WachtRij][] = []
  for (const r of rijen ?? []) {
    if (!r.inbox_claim_mine || !r.inbox_claim_active || r.inbox_claim_state !== 'replied') continue
    const tot = new Date(String(r.inbox_claim_until ?? '')).getTime()
    if (!Number.isFinite(tot)) continue
    uit.push([`${r.id}|${r.inbox_claim_until}`, tot - WAARSCHUW_MS, r])
  }
  return uit
}

/** De 2-minutenwaarschuwing (toast rechtsonder, niet blokkerend). */
export function useClaimWaarschuwing(rijen: WachtRij[] | undefined): void {
  const { notify } = useActions()
  const navigate = useNavigate()
  const gewaarschuwd = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!rijen) return
    const timers: number[] = []
    for (const [sleutel, moment, rij] of waarschuwMomenten(rijen)) {
      if (gewaarschuwd.current.has(sleutel)) continue
      const tot = moment + WAARSCHUW_MS
      if (Date.now() >= tot) continue                 // al voorbij: geen melding meer
      const toon = () => {
        if (gewaarschuwd.current.has(sleutel)) return
        gewaarschuwd.current.add(sleutel)
        const fan = rij.fan_display_name || rij.fan_username || 'this fan'
        notify(`2 minutes left to answer ${fan} or the chat returns to the queue.`, true,
               { waarschuwing: true, duur: 20000, onClick: () => navigate(`/conversations/show/${rij.id}`) })
      }
      timers.push(window.setTimeout(toon, Math.max(0, moment - Date.now())))
    }
    // Nieuwe rijen (bv. de chatter antwoordde): oude timers weg, dus geen melding meer.
    return () => timers.forEach((t) => window.clearTimeout(t))
  }, [rijen, notify, navigate])
}
